// MF-568 — Pure helpers shared by the PAT skills cards (response chart, difficulty-aware matrix, next
// learning, growth quadrants): matrix building, step line, window classification, formatting.
import { INK, SUMMARY_PALETTE } from '../boardTheme'

export const RESPONSE_STYLE = {
  C: { bg: '#d3e0f1', fg: '#24407a', label: 'Correct' },
  X: { bg: '#f6dcc2', fg: '#7a3e12', label: 'Incorrect' },
  '-': { bg: '#eef0f4', fg: SUMMARY_PALETTE.faint, label: 'Not administered' }
}

// Relative challenge of an item for a student (3B): three soft colours, never the only signal (title + legend).
export const WINDOW_STYLE = {
  below: { bg: '#e4eaf3', label: 'Below student window' },
  within: { bg: '#b9d0ea', label: 'Within student window' },
  above: { bg: '#fbe0c3', label: 'Above student window' }
}

export const fmtD = (n) => {
  if (n === null || n === undefined || n === '' || !Number.isFinite(Number(n))) return '–'
  const v = Number(n)
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

export const normResponse = (r) => (r === 'C' || r === 'X' ? r : '-')

export const classifyItem = (difficulty, score, window) => {
  const d = Number(difficulty)
  const s = Number(score)
  const w = Number(window)
  if (
    difficulty === null ||
    difficulty === undefined ||
    !Number.isFinite(d) ||
    !Number.isFinite(s) ||
    !Number.isFinite(w)
  )
    return null
  if (d < s - w) return 'below'
  if (d > s + w) return 'above'
  return 'within'
}

export const strandInitials = (strand) => {
  const words = String(strand || '')
    .split(/[\s/&,-]+/)
    .filter(Boolean)
  if (!words.length) return '?'
  return words.length === 1
    ? words[0].slice(0, 2)
    : words
        .slice(0, 3)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
}

/** Index of the last item (in the given order) with difficulty <= score; -1 when none. Null difficulties are skipped. */
export const stepIndex = (items, score) => {
  let idx = -1
  items.forEach((it, i) => {
    if (it.difficulty !== null && it.difficulty !== undefined && Number(it.difficulty) <= Number(score))
      idx = i
  })
  return idx
}

const byDifficulty = (a, b) => {
  const an = a.difficulty === null || a.difficulty === undefined
  const bn = b.difficulty === null || b.difficulty === undefined
  if (an !== bn) return an ? 1 : -1
  return an ? 0 : a.difficulty - b.difficulty
}

/**
 * rows (long: student × item) → { items, students, cell(studentId, itemUid) , strands }.
 * Items ordered by difficulty ascending (null last), or grouped by strand (each strand by difficulty) when
 * groupByStrand. Students by score descending. Missing pairs read as '-'.
 */
export const buildMatrix = (rows = [], { groupByStrand = false } = {}) => {
  const itemMap = new Map()
  const studentMap = new Map()
  const cells = new Map()
  for (const r of rows) {
    if (r.item_uid !== null && r.item_uid !== undefined && !itemMap.has(r.item_uid)) {
      itemMap.set(r.item_uid, {
        uid: r.item_uid,
        difficulty: r.difficulty === null || r.difficulty === undefined ? null : Number(r.difficulty),
        strand: r.strand || 'Other',
        sub_strand: r.sub_strand || null,
        description: r.description || null
      })
    }
    if (!studentMap.has(r.student_id))
      studentMap.set(r.student_id, { id: r.student_id, name: r.name, score: Number(r.score) })
    if (r.item_uid !== null && r.item_uid !== undefined)
      cells.set(`${r.student_id}\u0000${r.item_uid}`, normResponse(r.response))
  }
  // An ADAPTIVE test gives each student different items: a shared item × student grid is then almost all "not administered".
  // Keep the items answered by enough students (at least 3, or a fifth of the class) and say how many were left out.
  const answered = new Map()
  for (const r of rows) {
    if (r.item_uid !== null && r.item_uid !== undefined) {
      if (!answered.has(r.item_uid)) answered.set(r.item_uid, new Set())
      answered.get(r.item_uid).add(r.student_id)
    }
  }
  const nStudents = studentMap.size
  const filled = [...answered.values()].reduce((a, set) => a + set.size, 0)
  const adaptive = itemMap.size > 0 && nStudents > 1 && filled / (itemMap.size * nStudents) < 0.5
  const minShared = adaptive ? Math.max(3, Math.ceil(nStudents * 0.2)) : 1
  let hidden = 0
  if (adaptive) {
    for (const uid of [...itemMap.keys()]) {
      if (answered.get(uid).size < minShared) {
        itemMap.delete(uid)
        hidden += 1
      }
    }
  }
  let items = [...itemMap.values()].sort(byDifficulty)
  const strands = []
  if (groupByStrand) {
    const order = new Map()
    items.forEach((it) => {
      if (!order.has(it.strand)) order.set(it.strand, order.size)
    })
    items = [...items].sort((a, b) => order.get(a.strand) - order.get(b.strand) || byDifficulty(a, b))
  }
  items = items.map((it, i) => ({ ...it, idx: i + 1 }))
  items.forEach((it, i) => {
    const last = strands[strands.length - 1]
    if (last && last.strand === it.strand) last.span += 1
    else strands.push({ strand: it.strand, span: 1, start: i })
  })
  const students = [...studentMap.values()].sort(
    (a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name))
  )
  return {
    items,
    students,
    strands,
    adaptive,
    minShared,
    hidden,
    cell: (sid, uid) => cells.get(`${sid}\u0000${uid}`) || '-'
  }
}

/** The note shown over an item grid of an adaptive test (null when every student sat the same items). */
export const adaptiveNote = (m) =>
  m.adaptive
    ? m.items.length
      ? `This test is adaptive: students answered different items. Showing the ${m.items.length} items answered by at least ${m.minShared} of ${m.students.length} students; ${m.hidden} items answered by fewer are hidden.`
      : `This test is adaptive: students answered different items and no item was answered by at least ${m.minShared} of ${m.students.length} students, so a shared item grid would say nothing. Use the student report or the skills views instead.`
    : null

export const itemTitle = (it) =>
  [
    `Item ${it.idx}: ${it.uid}`,
    `Difficulty ${fmtD(it.difficulty)}`,
    `Skill area: ${it.strand}${it.sub_strand ? ` / ${it.sub_strand}` : ''}`,
    it.description
  ]
    .filter(Boolean)
    .join('\n')

export const GRID_INK = INK

export const GAP_TEXT =
  'No item in the bank is near every student of this group — select a more demanding/easier task after a short check, or request a calibrated task or teacher assessment.'

/** 3C rows → { strands: [..], groups: [{ group_no, min, max, size, students, cells: Map(strand → row), notes: [...] }] } */
export const buildNextLearning = (rows = []) => {
  const strands = []
  const groups = new Map()
  for (const r of rows) {
    const strand = r.strand || 'Other'
    if (!strands.includes(strand)) strands.push(strand)
    if (!groups.has(r.group_no)) {
      groups.set(r.group_no, {
        group_no: r.group_no,
        min: r.group_min,
        max: r.group_max,
        size: r.group_size,
        students: r.students || '',
        cells: new Map(),
        notes: []
      })
    }
    const g = groups.get(r.group_no)
    g.cells.set(strand, r)
    if (r.teaching_note && !g.notes.includes(r.teaching_note)) g.notes.push(r.teaching_note)
  }
  return { strands, groups: [...groups.values()].sort((a, b) => a.group_no - b.group_no) }
}

export const median = (xs) => {
  const v = xs.filter((x) => Number.isFinite(x)).sort((a, b) => a - b)
  if (!v.length) return null
  const m = Math.floor(v.length / 2)
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2
}

export const QUADRANTS = {
  optimal: { label: 'Optimal', hint: 'high growth / high score', color: '#2A69A7' },
  growth: { label: 'Growth', hint: 'high growth / low score', color: '#87A9D7' },
  cruising: { label: 'Cruising', hint: 'low growth / high score', color: '#F3A452' },
  support: { label: 'Needs additional support', hint: 'low growth / low score', color: '#B45B23' }
}
export const QUADRANT_KEYS = ['optimal', 'growth', 'cruising', 'support']

/** Quadrant from the two thresholds; null when the benchmark is unknown. */
export const quadrantOf = (growth, score, threshold, benchmark) => {
  if (
    benchmark === null ||
    benchmark === undefined ||
    !Number.isFinite(Number(growth)) ||
    !Number.isFinite(Number(score))
  )
    return null
  const hiG = Number(growth) >= Number(threshold)
  const hiS = Number(score) >= Number(benchmark)
  return hiG ? (hiS ? 'optimal' : 'growth') : hiS ? 'cruising' : 'support'
}

/** The benchmark shared by ALL rows, else null (missing or mixed). */
export const sharedBenchmark = (rows = []) => {
  if (!rows.length) return null
  const vals = rows.map((r) =>
    r.benchmark === null || r.benchmark === undefined ? null : Number(r.benchmark)
  )
  if (vals.some((v) => v === null || !Number.isFinite(v))) return null
  return vals.every((v) => v === vals[0]) ? vals[0] : null
}

export const esc = (s) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  )
